import { useSendMeetingEmailMutation } from '@consigli/hooks';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { EmailContentOption } from '../components/email-options-modal';

interface EmailMeeting {
  id: string;
  title: string;
}

export const useEmailMeetingSummary = (meeting: EmailMeeting, projectId: number | string) => {
  const { t, i18n } = useTranslation();
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [sendMeetingEmail] = useSendMeetingEmailMutation();
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleEmailSummary = useCallback(
    async (contentOption: EmailContentOption, recipients: string[]): Promise<boolean> => {
      // Basic check: ensure we have a meeting ID
      if (!meeting?.id) {
        toast.warn(t('meeting-secretary.meetings.email-error-no-data'));
        return false;
      }

      setIsSendingEmail(true);
      abortControllerRef.current = new AbortController();

      try {
        const requestPayload = {
          meetingId: meeting.id,
          projectId: String(projectId),
          contentOption,
          recipientEmails: recipients,
          language: i18n.language,
        };

        // Call backend to format and send email
        const response = await sendMeetingEmail(requestPayload).unwrap();

        if (response.status === 'success') {
          toast.success(t('meeting-secretary.meetings.email-sent-success'));
          return true;
        } else {
          toast.error(t('meeting-secretary.meetings.email-error-failed'));
          return false;
        }
      } catch (error) {
        // Don't show error if it was aborted by user
        const isAbortError = error instanceof Error && error.name === 'AbortError';
        if (!isAbortError) {
          toast.error(t('meeting-secretary.meetings.email-error-failed'));
        }
        return false;
      } finally {
        setIsSendingEmail(false);
        abortControllerRef.current = null;
      }
    },
    [meeting, projectId, t, sendMeetingEmail, i18n.language],
  );

  const cancelEmail = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsSendingEmail(false);
  }, []);

  return {
    handleEmailSummary,
    isSendingEmail,
    cancelEmail,
  };
};
